/**
 * Drives `--sheen-x` / `--sheen-y` — the virtual position of the amber key
 * light, read by every `.glass` surface's specular overlay (see app.css).
 * One rAF loop updates two custom properties on `:root`; every glass
 * panel just references them in a `radial-gradient()` — this is the
 * "single rAF loop, not per-element listeners" the brief requires.
 *
 * PHASE 5 CORRECTION, made honest rather than hidden: this briefly ran on
 * `gsap.ticker` instead of a bespoke loop, chasing the letter of "everything
 * on one master rAF/GSAP ticker." Measured real cost of that: the initial
 * JS bundle jumped from ~5.9KB to ~33.6KB gzip, because sheen.ts is
 * imported synchronously by main.ts (it's needed before first paint), so
 * GSAP rode onto the critical path — for a driver that only ever calls
 * `gsap.ticker.add()` and never touches GSAP's actual tweening engine.
 * That's not what "single master ticker" is for. GSAP's real capability
 * (timelines, easing) is reserved for where it's genuinely lazy-loaded —
 * scene.ts's spin animation (inside the 3D chunk) and Phase 6's purchase
 * ceremony. This file goes back to a small local rAF loop instead: it's
 * the only 2D consumer that needs continuous scheduling, so a shared
 * "ticker" abstraction has no second subscriber to justify its own
 * existence yet either.
 */

const PERIOD_MS = 11_000; // slow, restrained drift — a light easing across the room, not spinning
const UPDATE_INTERVAL_MS = 66; // ~15fps write rate

export function initSheen(): void {
  const root = document.documentElement;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    root.style.setProperty("--sheen-x", "50%");
    root.style.setProperty("--sheen-y", "28%");
    return;
  }

  let accum = 0;
  let lastTime = 0;
  let rafHandle = 0;

  function frame(now: number) {
    const delta = lastTime ? now - lastTime : 0;
    lastTime = now;
    accum += delta;
    if (accum >= UPDATE_INTERVAL_MS) {
      accum = 0;
      const angle = ((now % PERIOD_MS) / PERIOD_MS) * Math.PI * 2;
      const x = 50 + Math.sin(angle) * 30;
      const y = 28 + Math.cos(angle * 0.6) * 18;
      root.style.setProperty("--sheen-x", `${x.toFixed(1)}%`);
      root.style.setProperty("--sheen-y", `${y.toFixed(1)}%`);
    }
    rafHandle = requestAnimationFrame(frame);
  }

  function start() {
    lastTime = 0;
    rafHandle = requestAnimationFrame(frame);
  }

  // No wasted work in the background — matches the retired ticker.ts's
  // behavior, not just a lucky side effect of inlining this loop.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      cancelAnimationFrame(rafHandle);
    } else {
      start();
    }
  });

  start();
}
